import { useState } from 'react';
import { apiGet, apiPut, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { StatusBadge } from '../components/StatusBadge';

/** GET/PUT /operational-settings/admin (OperationalSettingsAdminController). */
interface OperationalSettings {
  userApprovalTimeoutMs: number;
  aiDailyTokenBudgetPerUser: number;
  proactiveMaxMessagesPerUserPerDay: number;
  proactiveMinHoursBetweenMessages: number;
  proactiveInferenceCacheTtlHours: number;
  liveServiceOfferTtlMs: number;
  asyncOfferApprovalWindowHours: number;
  asyncOfferSlaHours: number;
  specialistReviewWindowMs: number;
  specialistPushTapWindowMs: number;
  specialistPushReviewWindowMs: number;
  serviceCardTtlMs: number;
  /** ISO country code of the market used when nothing about a specialist names one. */
  defaultMarketCode: string;
}

/** Every setting edited as a whole number within a range — all but the market. */
type SettingKey = Exclude<keyof OperationalSettings, 'defaultMarketCode'>;

interface MarketOption {
  code: string;
  name: string;
  currency: string;
}

interface SettingsResponse {
  settings: OperationalSettings;
  /** Bounds come from the backend so the portal never keeps its own copy. */
  limits: Record<SettingKey, { min: number; max: number }>;
  /** Markets the default may name — also from the backend, never a local list. */
  markets?: MarketOption[];
}

const seconds = (ms: number) => `${Math.round(ms / 1000)} s`;

interface FieldSpec {
  label: string;
  unit: string;
  help: string;
  /** Renders the stored value in human terms, e.g. 180000 ms → "3 min". */
  describe: (value: number) => string;
}

const FIELDS: Record<SettingKey, FieldSpec> = {
  userApprovalTimeoutMs: {
    label: 'Approval window',
    unit: 'ms',
    help: 'How long a patient has to approve and pay before the matched specialist is released back to the pool. Also drives the countdown shown in the app.',
    describe: (ms) => `${(ms / 1000 / 60).toFixed(1)} min`,
  },
  aiDailyTokenBudgetPerUser: {
    label: 'AI token budget',
    unit: 'tokens / user / day',
    help: 'Soft daily cap on total tokens across all AI tasks. Guards against runaway loops and abuse — a user who hits it stops getting AI responses until the next day.',
    describe: (tokens) => `${(tokens / 1000).toLocaleString()}k tokens`,
  },
  proactiveMaxMessagesPerUserPerDay: {
    label: 'Proactive messages',
    unit: 'per user / day',
    help: 'Most proactive agent messages one user may receive in a day. Set to 0 to turn proactive messaging off entirely.',
    describe: (count) => (count === 0 ? 'disabled' : `${count} per day`),
  },
  proactiveMinHoursBetweenMessages: {
    label: 'Proactive spacing',
    unit: 'hours',
    help: 'Minimum gap between proactive messages to the same user.',
    describe: (hours) => (hours === 0 ? 'no minimum' : `${hours} h`),
  },
  proactiveInferenceCacheTtlHours: {
    label: 'Inference cache TTL',
    unit: 'hours',
    help: 'How long a proactive inference result is reused before being recomputed.',
    describe: (hours) => (hours === 0 ? 'no caching' : `${hours} h`),
  },
  liveServiceOfferTtlMs: {
    label: 'Live offer window',
    unit: 'ms',
    help: "How long a patient has to accept a specialist's offer for a live service (telemedicine, counseling). The offer card in the app drains over exactly this window.",
    describe: seconds,
  },
  asyncOfferApprovalWindowHours: {
    label: 'Async offer window',
    unit: 'hours',
    help: 'How long a patient has to accept an offer for an async service (case review, second opinion).',
    describe: (hours) => `${hours} h`,
  },
  asyncOfferSlaHours: {
    label: 'Async offer turnaround',
    unit: 'hours',
    help: 'Turnaround promised once an async-service offer is accepted — its SLA deadline.',
    describe: (hours) => `${hours} h`,
  },
  specialistReviewWindowMs: {
    label: 'Specialist review window',
    unit: 'ms',
    help: 'How long a specialist notified in the app has to accept or pass a new case before it moves to the next specialist.',
    describe: seconds,
  },
  specialistPushTapWindowMs: {
    label: 'Push tap window',
    unit: 'ms',
    help: 'How long a specialist notified by push has to tap the notification (locked phone, cold start). Missed → the case moves on.',
    describe: seconds,
  },
  specialistPushReviewWindowMs: {
    label: 'Push review window',
    unit: 'ms',
    help: 'Once a push-notified specialist opens the case, how long they have to accept or pass.',
    describe: seconds,
  },
  serviceCardTtlMs: {
    label: 'Service card lifetime',
    unit: 'ms',
    help: "How long Dr. Lynkia's paid service cards stay actionable in the chat. The card drains over exactly this window.",
    describe: seconds,
  },
};

const FIELD_KEYS = Object.keys(FIELDS) as SettingKey[];

/**
 * ADM ops — Operational Settings.
 *
 * These values were environment variables or code constants until they were moved into
 * Firestore: changing the approval window or the AI token budget meant a code
 * change and a redeploy, for numbers that decide what a patient sees and what
 * the platform spends. Moving them to the database made them changeable; this
 * page is what makes them changeable *by an admin* rather than by someone with
 * database access.
 *
 * The allowed range for each field is read from the backend alongside the value
 * rather than duplicated here, so the bounds shown can't drift from the bounds
 * enforced. A value outside its range is rejected server-side, not clamped, so
 * the input is blocked here before it can produce a confusing partial save.
 */
export function OperationalSettingsPage() {
  const {
    data,
    loading,
    error,
    reload,
  } = useApiResource<SettingsResponse>(() => apiGet<SettingsResponse>('/operational-settings/admin'), []);

  const [drafts, setDrafts] = useState<Partial<Record<SettingKey, string>>>({});
  const [marketDraft, setMarketDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const limits = data?.limits;
  const settings = data?.settings;

  /** The effective value for a field: the draft if edited, else what's stored. */
  const valueOf = (key: SettingKey): string =>
    drafts[key] ?? (settings ? String(settings[key]) : '');

  const parsed = (key: SettingKey): number => Number(drafts[key] ?? settings?.[key] ?? NaN);

  const invalidKeys = FIELD_KEYS.filter((key) => {
    if (!(key in drafts)) return false;
    const value = parsed(key);
    if (!Number.isFinite(value) || !Number.isInteger(value)) return true;
    const range = limits?.[key];
    return !!range && (value < range.min || value > range.max);
  });

  const changedKeys = FIELD_KEYS.filter(
    (key) => key in drafts && parsed(key) !== settings?.[key],
  );
  const marketChanged = marketDraft !== null && marketDraft !== settings?.defaultMarketCode;
  const changeCount = changedKeys.length + (marketChanged ? 1 : 0);

  const save = async () => {
    if (!settings || changeCount === 0) return;
    if (invalidKeys.length > 0) {
      setActionError('Every changed field must be a whole number inside its allowed range.');
      return;
    }

    setSaving(true);
    setActionError(null);
    try {
      // Partial PUT — only what changed, so an untouched field can't be
      // clobbered by a stale value this page happened to be holding.
      const patch: Record<string, number | string> = Object.fromEntries(
        changedKeys.map((key) => [key, parsed(key)]),
      );
      if (marketChanged && marketDraft) patch.defaultMarketCode = marketDraft;
      await apiPut('/operational-settings/admin', patch);
      setDrafts({});
      setMarketDraft(null);
      setSavedAt(Date.now());
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="empty-state">Loading operational settings…</div>;
  if (error) return <div className="error-banner">{error}</div>;
  // The registry types pages as `() => Element`, and a bare `null` would render
  // a blank panel with no explanation anyway.
  if (!settings || !limits) {
    return (
      <div className="empty-state" data-testid="operational-settings-empty">
        No operational settings returned by the server.
      </div>
    );
  }

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Operational Settings</h1>
          <p>
            Business rules that used to require a redeploy to change. Edits take effect
            immediately — no deploy, no restart.
          </p>
        </div>
      </div>

      {savedAt && !actionError && (
        <div className="result-card" data-testid="operational-settings-saved">
          Settings saved and applied.
        </div>
      )}

      {actionError && (
        <div className="error-banner" data-testid="operational-settings-error">
          {actionError}
        </div>
      )}

      <div className="form-card full-width">
        <h2>Limits and windows</h2>

        <div className="setting-list">
          {FIELD_KEYS.map((key) => {
            const spec = FIELDS[key];
            const range = limits[key];
            const isInvalid = invalidKeys.includes(key);
            const isChanged = changedKeys.includes(key);

            return (
              <div className="setting-row" key={key} data-testid={`operational-setting-${key}`}>
                <div className="setting-row-text">
                  <div className="setting-row-name">
                    {spec.label}
                    {isChanged && !isInvalid && (
                      <>
                        {' '}
                        <StatusBadge label="Unsaved" tone="warn" />
                      </>
                    )}
                  </div>
                  <div className="setting-row-hint">{spec.help}</div>
                </div>

                <div className="setting-row-number">
                  <div className="setting-row-input">
                    <input
                      type="number"
                      inputMode="numeric"
                      value={valueOf(key)}
                      min={range.min}
                      max={range.max}
                      aria-label={spec.label}
                      aria-invalid={isInvalid}
                      onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                      className={isInvalid ? 'invalid' : undefined}
                    />
                    <span className="setting-row-unit">{spec.unit}</span>
                  </div>

                  <div className={`setting-row-value${isInvalid ? ' invalid' : ''}`}>
                    {isInvalid ? (
                      <>
                        must be a whole number between {range.min.toLocaleString()} and{' '}
                        {range.max.toLocaleString()}
                      </>
                    ) : (
                      <>
                        {spec.describe(parsed(key))}
                        <span className="bounds">
                          {' '}
                          · allowed {range.min.toLocaleString()}–{range.max.toLocaleString()}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="form-card full-width" data-testid="operational-setting-defaultMarketCode">
        <h2>Default market</h2>
        <div className="setting-row">
          <div className="setting-row-text">
            <div className="setting-row-name">
              Default market
              {marketChanged && (
                <>
                  {' '}
                  <StatusBadge label="Unsaved" tone="warn" />
                </>
              )}
            </div>
            <div className="setting-row-hint">
              Used when nothing about a specialist (profile country, licences or payout
              account) names a market. It decides the currency patients are charged in.
            </div>
          </div>
          <div className="setting-row-number">
            <select
              aria-label="Default market"
              value={marketDraft ?? settings.defaultMarketCode ?? ''}
              onChange={(e) => setMarketDraft(e.target.value)}
            >
              {(data?.markets ?? []).map((m) => (
                <option key={m.code} value={m.code}>
                  {m.name} ({m.currency})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="section-toolbar">
        <button
          type="button"
          onClick={save}
          disabled={saving || changeCount === 0 || invalidKeys.length > 0}
          data-testid="operational-settings-save"
          className="btn btn-primary"
        >
          {saving ? 'Saving…' : `Save${changeCount ? ` (${changeCount})` : ''}`}
        </button>
        {changeCount > 0 && (
          <button
            type="button"
            onClick={() => {
              setDrafts({});
              setMarketDraft(null);
              setActionError(null);
            }}
            className="btn btn-secondary"
          >
            Discard changes
          </button>
        )}
      </div>
    </div>
  );
}
